/**
 * Profile Query Hooks
 *
 * Reusable TanStack Query hooks for user profile operations using the CC Platform SDK.
 * Handles profile data, posts, and likes with infinite scroll.
 */

import { useQuery } from "@tanstack/vue-query";
import { computed, type ComputedRef, type Ref } from "vue";
import { useSdk } from "@/composables/useSdk";
import { useInfiniteFeedList } from "@/composables/useInfiniteFeedList";
import type { Post, UserProfile } from "@cc-consulting-nv/ccsdk";
import { queryKeys } from "./queryKeys";
import { debugLog } from "@/utils/debug";

/**
 * Feed response structure
 */
interface FeedResponse {
  posts: Post[];
  nextCursor?: string | null;
}

/**
 * Hook for fetching user profile by username
 *
 * @param username - The username to fetch (can be reactive)
 * @example
 * const username = ref('johndoe')
 * const { user, isLoading, error } = useUserProfile(username)
 */
export function useUserProfile(
  username: Ref<string> | ComputedRef<string> | string,
) {
  const { sdk } = useSdk();

  const usernameValue = computed(() =>
    typeof username === "object" && "value" in username
      ? username.value
      : username,
  );

  const query = useQuery({
    queryKey: computed(() => queryKeys.users.profile(usernameValue.value)),
    queryFn: async () => {
      debugLog("api", "useUserProfile queryFn", {
        username: usernameValue.value,
      });

      const response = await sdk.getUserProfile(usernameValue.value);
      // Unwrap if needed
      const profile = "data" in response ? response.data : response;
      return profile as UserProfile;
    },
    enabled: computed(() => !!usernameValue.value),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  return {
    user: computed(() => query.data.value),
    isLoading: computed(() => query.isLoading.value),
    error: computed(() => query.error.value),
    refetch: query.refetch,
  };
}

/**
 * Hook for user posts with infinite scroll
 *
 * @param username - The username (can be reactive)
 * @param options - Optional configuration
 * @example
 * const { posts, fetchNextPage, hasNextPage } = useUserPosts(username)
 */
export function useUserPosts(
  userId: Ref<string> | ComputedRef<string> | string,
  options: {
    enabled?: ComputedRef<boolean> | boolean;
  } = {},
) {
  const { enabled = true } = options;
  const { sdk } = useSdk();

  const userIdValue = computed(() =>
    typeof userId === "object" && "value" in userId ? userId.value : userId,
  );

  const enabledValue = computed(() =>
    typeof enabled === "object" ? enabled.value : enabled,
  );

  const {
    items: posts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  } = useInfiniteFeedList<FeedResponse, Post, string | undefined>({
    queryKey: computed(() => queryKeys.profile.posts(userIdValue.value)),
    enabled: computed(() => enabledValue.value && !!userIdValue.value),
    queryFn: async ({ pageParam }): Promise<FeedResponse> => {
      debugLog("api", "useUserPosts queryFn", {
        userId: userIdValue.value,
        pageParam,
      });
      const response = await sdk.getUserPosts(userIdValue.value, pageParam);
      return {
        posts: response.posts || [],
        nextCursor: response.nextCursor ?? undefined,
      };
    },
    selectItems: (page) => page.posts,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    posts,
    isLoading: computed(() => isLoading.value),
    isFetchingNextPage: computed(() => isFetchingNextPage.value),
    hasNextPage: computed(() => hasNextPage.value ?? false),
    error: computed(() => error.value as Error | null),
    fetchNextPage,
    refetch,
  };
}

/**
 * Hook for user songs with infinite scroll (music sites)
 * Uses getUserMedia for media content
 *
 * @param userId - The user's ULID (can be reactive)
 * @param options - Optional configuration
 * @example
 * const { posts, fetchNextPage, hasNextPage } = useUserSongs(userId)
 */
export function useUserSongs(
  userId: Ref<string> | ComputedRef<string> | string,
  options: {
    enabled?: ComputedRef<boolean> | boolean;
  } = {},
) {
  const { enabled = true } = options;
  const { sdk } = useSdk();

  const userIdValue = computed(() =>
    typeof userId === "object" && "value" in userId ? userId.value : userId,
  );

  const enabledValue = computed(() =>
    typeof enabled === "object" ? enabled.value : enabled,
  );

  const {
    items: posts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  } = useInfiniteFeedList<FeedResponse, Post, string | undefined>({
    queryKey: computed(() => queryKeys.profile.songs(userIdValue.value)),
    enabled: computed(() => enabledValue.value && !!userIdValue.value),
    queryFn: async ({ pageParam }): Promise<FeedResponse> => {
      debugLog("api", "useUserSongs queryFn", {
        userId: userIdValue.value,
        pageParam,
      });
      const response = await sdk.getUserMedia(userIdValue.value, pageParam);
      return {
        posts: response.posts || [],
        nextCursor: response.nextCursor ?? undefined,
      };
    },
    selectItems: (page) => page.posts,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    posts,
    isLoading: computed(() => isLoading.value),
    isFetchingNextPage: computed(() => isFetchingNextPage.value),
    hasNextPage: computed(() => hasNextPage.value ?? false),
    error: computed(() => error.value as Error | null),
    fetchNextPage,
    refetch,
  };
}

/**
 * Hook for user liked posts with infinite scroll
 *
 * @param userId - The user's ULID (can be reactive)
 * @param options - Optional configuration
 * @example
 * const { posts, fetchNextPage, hasNextPage } = useUserLikes(userId)
 */
export function useUserLikes(
  userId: Ref<string> | ComputedRef<string> | string,
  options: {
    enabled?: ComputedRef<boolean> | boolean;
  } = {},
) {
  const { enabled = true } = options;
  const { sdk } = useSdk();

  const userIdValue = computed(() =>
    typeof userId === "object" && "value" in userId ? userId.value : userId,
  );

  const enabledValue = computed(() =>
    typeof enabled === "object" ? enabled.value : enabled,
  );

  const {
    items: posts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    refetch,
  } = useInfiniteFeedList<FeedResponse, Post, string | undefined>({
    queryKey: computed(() => queryKeys.profile.likes(userIdValue.value)),
    enabled: computed(() => enabledValue.value && !!userIdValue.value),
    queryFn: async ({ pageParam }): Promise<FeedResponse> => {
      debugLog("api", "useUserLikes queryFn", {
        userId: userIdValue.value,
        pageParam,
      });
      const response = await sdk.getUserLikes(userIdValue.value, pageParam);
      return {
        posts: response.posts || [],
        nextCursor: response.nextCursor ?? undefined,
      };
    },
    selectItems: (page) => page.posts,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    posts,
    isLoading: computed(() => isLoading.value),
    isFetchingNextPage: computed(() => isFetchingNextPage.value),
    hasNextPage: computed(() => hasNextPage.value ?? false),
    error: computed(() => error.value as Error | null),
    fetchNextPage,
    refetch,
  };
}

/**
 * Hook for user followers
 * Note: SDK returns ApiEnvelope, not paginated feed
 *
 * @param userId - The user's ULID
 * @param options - Optional configuration
 * @example
 * const { users, isLoading } = useUserFollowers(userUlid)
 */
export function useUserFollowers(
  userId: Ref<string> | ComputedRef<string> | string,
  options: {
    enabled?: ComputedRef<boolean> | boolean;
  } = {},
) {
  const { enabled = true } = options;
  const { sdk } = useSdk();

  const userIdValue = computed(() =>
    typeof userId === "object" && "value" in userId ? userId.value : userId,
  );

  const enabledValue = computed(() =>
    typeof enabled === "object" ? enabled.value : enabled,
  );

  const query = useQuery({
    queryKey: computed(() => queryKeys.users.followers(userIdValue.value)),
    enabled: computed(() => enabledValue.value && !!userIdValue.value),
    queryFn: async () => {
      debugLog("api", "useUserFollowers queryFn", {
        userId: userIdValue.value,
      });

      const response = await sdk.getUserFollowers(userIdValue.value);
      // Unwrap if needed
      const data = "data" in response ? response.data : response;
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });

  return {
    users: computed(() => query.data.value ?? []),
    isLoading: computed(() => query.isLoading.value),
    error: computed(() => query.error.value),
    refetch: query.refetch,
  };
}

/**
 * Hook for user following
 * Note: SDK returns ApiEnvelope, not paginated feed
 *
 * @param userId - The user's ULID
 * @param options - Optional configuration
 * @example
 * const { users, isLoading } = useUserFollowing(userUlid)
 */
export function useUserFollowing(
  userId: Ref<string> | ComputedRef<string> | string,
  options: {
    enabled?: ComputedRef<boolean> | boolean;
  } = {},
) {
  const { enabled = true } = options;
  const { sdk } = useSdk();

  const userIdValue = computed(() =>
    typeof userId === "object" && "value" in userId ? userId.value : userId,
  );

  const enabledValue = computed(() =>
    typeof enabled === "object" ? enabled.value : enabled,
  );

  const query = useQuery({
    queryKey: computed(() => queryKeys.users.following(userIdValue.value)),
    enabled: computed(() => enabledValue.value && !!userIdValue.value),
    queryFn: async () => {
      debugLog("api", "useUserFollowing queryFn", {
        userId: userIdValue.value,
      });

      const response = await sdk.getUserFollowing(userIdValue.value);
      // Unwrap if needed
      const data = "data" in response ? response.data : response;
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });

  return {
    users: computed(() => query.data.value ?? []),
    isLoading: computed(() => query.isLoading.value),
    error: computed(() => query.error.value),
    refetch: query.refetch,
  };
}
